import { APITypesV1 } from "@cur8/api-client";
import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { Visit } from "@cur8/rich-entity";
import { useLatestConsentsQuery } from "hooks/queries/usePatientConsentsQuery";
import { usePatientQuery } from "hooks/queries/usePatientQuery";
import { useRoomQueries } from "hooks/queries/useRoomQuery";
import { useVisitBloodSamplesQuery } from "hooks/queries/useVisitBloodSamplesQuery";
import { useVisitHistoryQuery } from "hooks/queries/useVisitHistoryQuery";
import { markAsActiveSession } from "lib/activeSession";
import { formatTimeRange } from "lib/formatDateTime";
import { formatMemberName } from "lib/formatMemberName";
import { generateVisitTypeLabel } from "lib/generateVisitTypeLabel";
import { groupVisitsByRoom, VisitRoomGroup } from "lib/groupVisitsByRoom";
import {
  everyBloodSampleStatusResolved,
  everyLimsTubeRecorded,
  hasLimsManagedSamples,
} from "lib/tubeStatus";
import { useMemo, useState } from "react";
import { useTodaysBookedVisits } from "render/hooks/useTodaysBookedVisits";
import { nextStepsUrl } from "render/routes/nextStepsUrl";
import { ActionsFooter } from "render/ui/layout/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent";
import { RadioGroup } from "render/ui/presentation/RadioGroup";
import { ScheduleGate } from "render/ui/presentation/ScheduleGate";
import styles from "./styles.module.sass";

export function SelectActiveVisit({
  goTo,
  onBack,
}: {
  goTo: (url: string) => void;
  onBack(): void;
}) {
  const schedule = useTodaysBookedVisits();
  const activeVisits = schedule.visits
    .filter(
      (visit) =>
        visit.status === APITypesV1.VisitStatus.CheckedIn ||
        visit.status === APITypesV1.VisitStatus.Ongoing ||
        visit.status === APITypesV1.VisitStatus.Completed
    )
    .sort((a, b) => (a.startTime < b.startTime ? -1 : 1));
  const checkedInVisits = activeVisits.filter(
    (visit) => visit.status === APITypesV1.VisitStatus.CheckedIn
  );
  const ongoingVisits = activeVisits.filter(
    (visit) => visit.status === APITypesV1.VisitStatus.Ongoing
  );
  const completedVisits = activeVisits.filter(
    (visit) => visit.status === APITypesV1.VisitStatus.Completed
  );
  const [selectedVisitId, setSelectedVisitId] = useState<string | undefined>(
    undefined
  );
  const roomIds = [...new Set(activeVisits.map((visit) => visit.roomId))];
  const roomQueries = useRoomQueries(roomIds);
  // Hold the list until rooms resolve, so visits don't flash under
  // "Unassigned" (re-ambiguating same-named members) before regrouping.
  const roomsLoading = roomQueries.some((query) => query.isLoading);
  const rooms = roomQueries
    .filter((room) => room.data != null)
    .map((room) => room.data);
  const checkedInRoomGroups = groupVisitsByRoom(checkedInVisits, rooms);
  const ongoingRoomGroups = groupVisitsByRoom(ongoingVisits, rooms);
  const completedRoomGroups = groupVisitsByRoom(completedVisits, rooms);

  const selectedVisit = useMemo(() => {
    return activeVisits.find((visit) => visit.visitId === selectedVisitId);
  }, [selectedVisitId, activeVisits]);

  // Only a CheckedIn visit's route depends on latestConsents (the
  // countersign gate); Ongoing/Completed take a different branch entirely,
  // so don't fetch, or block Continue on, consents that won't be used.
  const isCheckedIn =
    selectedVisit?.status === APITypesV1.VisitStatus.CheckedIn;

  const latestConsentsQuery = useLatestConsentsQuery({
    patientId: selectedVisit?.patientId,
    options: { enabled: isCheckedIn },
  });
  const latestConsents = latestConsentsQuery.data;

  const isExtendedBloodVisit = selectedVisit?.extendedBloodPanel === true;

  const bloodSamplesQuery = useVisitBloodSamplesQuery({
    patientId: selectedVisit?.patientId ?? "",
    visitId: selectedVisit?.visitId ?? "",
    options: { enabled: isCheckedIn && isExtendedBloodVisit },
  });

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <div className={styles.title}>
              <Typography variant="display-s">Active sessions</Typography>
              <Typography variant="body-m" color="subtle">
                Select the member whose session you'd like to restore.
              </Typography>
            </div>
            <ScheduleGate schedule={schedule} busy={roomsLoading}>
              <RadioGroup
                aria-label="select visit"
                onChange={(value: string) => setSelectedVisitId(value)}
              >
                <div className={styles.radioGroup}>
                  <VisitRoomSection
                    title="Checked-in"
                    emptyLabel="No checked-in visits"
                    roomGroups={checkedInRoomGroups}
                  />
                  <VisitRoomSection
                    title="Ongoing"
                    emptyLabel="No ongoing visits"
                    roomGroups={ongoingRoomGroups}
                  />
                  <VisitRoomSection
                    title="Completed"
                    emptyLabel="No completed visits"
                    roomGroups={completedRoomGroups}
                  />
                </div>
              </RadioGroup>
            </ScheduleGate>
          </section>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={onBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                state={
                  !selectedVisit
                    ? "disabled"
                    : isCheckedIn &&
                        (!latestConsentsQuery.isSuccess ||
                          (isExtendedBloodVisit &&
                            !bloodSamplesQuery.isSuccess))
                      ? "loading"
                      : undefined
                }
                onPress={() => {
                  if (!selectedVisit) {
                    return;
                  }
                  const {
                    patientId,
                    visitId,
                    medicalExams,
                    status,
                    consents,
                    extendedBloodPanel,
                  } = selectedVisit;

                  const nextUrl = nextStepsUrl({
                    patientId,
                    visitId,
                    status,
                    medicalExams,
                    visitConsents: consents,
                    latestConsents,
                    isExtendedBloodVisit: extendedBloodPanel,
                    hasLimsManagedBloodSamples: hasLimsManagedSamples(
                      bloodSamplesQuery.data?.bloodSamples
                    ),
                    isEveryLimsBloodTubeRecorded: everyLimsTubeRecorded(
                      bloodSamplesQuery.data?.bloodSamples
                    ),
                    isEveryBloodTubeResolved: everyBloodSampleStatusResolved(
                      bloodSamplesQuery.data?.bloodSamples
                    ),
                  });

                  goTo(markAsActiveSession(nextUrl));
                }}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Continue
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}

function VisitRoomSection({
  title,
  emptyLabel,
  roomGroups,
}: {
  title: string;
  emptyLabel: string;
  roomGroups: VisitRoomGroup[];
}) {
  return (
    <div className={styles.radios}>
      <Typography variant="title-m">{title}</Typography>
      {roomGroups.length > 0 ? (
        roomGroups.map((group) => (
          <div key={group.id} className={styles.roomGroup}>
            <Typography variant="eyebrow-m" color="subtle">
              {group.label}
            </Typography>
            {group.visits.map((visit) => (
              <VisitRadio key={visit.visitId} visit={visit} />
            ))}
          </div>
        ))
      ) : (
        <Typography variant="body-m">{emptyLabel}</Typography>
      )}
    </div>
  );
}

function VisitRadio({ visit }: { visit: Visit }) {
  const patientId = visit.patientId;
  const patientQuery = usePatientQuery({ patientId });
  const visitHistoryQuery = useVisitHistoryQuery({ patientId });
  const visitTypeLabel =
    visitHistoryQuery.data != null
      ? generateVisitTypeLabel({ visit, visitHistory: visitHistoryQuery.data })
      : undefined;

  // Operators restoring a session need the name the member goes by, so the
  // preferred name is shown inline: "Firstname (Preferred) Surname".
  const fullName = formatMemberName(patientQuery.data?.name);
  const countryCode = visit.site.address.countryCode?.toUpperCase();
  const time = formatTimeRange(visit.startTime, visit.endTime, countryCode);

  return (
    <RadioGroup.Radio
      key={visit.visitId}
      value={visit?.visitId}
      label={<span className={styles.memberName}>{fullName}</span>}
      bottomLeft={
        <Typography variant="body-m" as="span" color="subtle">
          {visitTypeLabel}
        </Typography>
      }
      bottomRight={
        <Typography variant="body-m" as="span" color="subtle">
          {time}
        </Typography>
      }
    />
  );
}
